import type { CarrierTripStopTask, CarrierWaypointEta } from "@/api/carrier";
import { emptyLaneCounts, type LaneCounts, taskLane } from "./task-type-meta";
import type { TaskType } from "@/api.types.v2";
import { isTaskOnCancellationHold } from "@/lib/cancellation-work-hold";
import { taskHasVisibleNote } from "@/lib/task-instructions";

/**
 * Display-ready stops for the Trip focus modal.
 *
 * Ported from admin-web's `dispatch-v2/trip-stops.ts` so a stop reads the same
 * in both consoles — same derived fields, same fallbacks, same ordering. The
 * only difference is the source shape: carrier waypoints carry their address on
 * the TASK (`addressLine1`/`addressCity`), where admin's carry a nested
 * `address` object.
 */

export type StopStatus = "pending" | "active" | "done";

export interface DerivedStop {
  id: string;
  seq: number;
  /** Best-effort street line from the stop's tasks. */
  addr: string;
  counts: LaneCounts;
  total: number;
  status: StopStatus;
  /** Formatted HH:MM, or null when no ETA is known. */
  eta: string | null;
  /** Who is being visited, from the first Task that names anyone. */
  contact: string | null;
  /** The booked window as "09:00–12:00", or null when nothing is booked. */
  window: string | null;
  /** Any Task here carries handling notes. */
  note: boolean;
  /** Any Task here carries a fragile package. */
  fragile: boolean;
  heldCount: number;
  allHeld: boolean;
  tasks: CarrierTripStopTask[];
}

const ETA_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

/** Format an ISO timestamp (or an already-short string) to local HH:MM. */
export function formatEta(raw?: string | null): string | null {
  if (!raw || typeof raw !== "string") return null;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw; // already a display string
  return parsed.toLocaleTimeString([], ETA_TIME_FORMAT);
}

/**
 * A stop is DONE when every task on it is terminally resolved — completed,
 * failed or cancelled. Failed work needs no further visit (a retry is its own
 * task), so excluding it would leave a stop showing 1/2 forever.
 */
export function isTaskDone(task: Pick<CarrierTripStopTask, "status">): boolean {
  return ["COMPLETED", "FAILED", "CANCELLED"].includes(
    task.status.toUpperCase()
  );
}

export function stopStatus(
  tasks: ReadonlyArray<Pick<CarrierTripStopTask, "status">>
): StopStatus {
  if (tasks.length === 0) return "pending";
  if (tasks.every(isTaskDone)) return "done";
  if (tasks.some((task) => task.status.toUpperCase() === "IN_PROGRESS")) {
    return "active";
  }
  return "pending";
}

/**
 * The street line, or "Unknown location".
 *
 * Deliberately NOT the waypoint's `locationKey` — that is the raw
 * `"43.6594,-79.3276"` the row is keyed on, which a dispatcher cannot check
 * against what a customer told them or read down a phone. admin-web says
 * "Unknown location" when there is no address, and so do we.
 */
function stopAddress(tasks: CarrierTripStopTask[]): string {
  const withLine = tasks.find((task) => task.addressLine1?.trim());
  if (!withLine?.addressLine1) return "Unknown location";

  const city = tasks.find((task) => task.addressCity?.trim())?.addressCity;
  const line = withLine.addressLine1.trim();
  return city ? `${line}, ${city.trim()}` : line;
}

/**
 * The parts of a waypoint this derivation reads.
 *
 * Deliberately narrower than `CarrierTripStop`: the trip list and the trip
 * detail carry slightly different waypoint shapes (one allows a null
 * `locationKey`), and the derivation touches neither. Naming what it needs
 * lets both callers pass their own shape without a cast.
 */
export interface DerivableWaypoint {
  id: string;
  sequence: number;
  eta?: CarrierWaypointEta | null;
  tasks?: CarrierTripStopTask[];
}

/**
 * The booked window, as the rest of Dispatch writes one.
 *
 * Only pickups are booked into a slot, so most stops have none — and a stop
 * with no window is not the same as one whose window nobody has looked up.
 * Both read as `null` here; the Server sends the field either way.
 */
function stopWindow(tasks: CarrierTripStopTask[]): string | null {
  const booked = tasks.find((task) => task.scheduledWindow);
  if (!booked?.scheduledWindow) return null;
  const { startTime, endTime } = booked.scheduledWindow;
  return startTime && endTime ? `${startTime}–${endTime}` : null;
}

/** The first contact named on any Task at this stop. */
function stopContact(tasks: CarrierTripStopTask[]): string | null {
  return (
    tasks.find((task) => task.contactName?.trim())?.contactName?.trim() ?? null
  );
}

/** Turn a trip's waypoints into ordered, display-ready stops. */
export function buildStops(
  waypoints: ReadonlyArray<DerivableWaypoint>
): DerivedStop[] {
  return [...waypoints]
    .sort((a, b) => a.sequence - b.sequence)
    .map((waypoint) => {
      const tasks = waypoint.tasks ?? [];
      const counts = emptyLaneCounts();
      for (const task of tasks) counts[taskLane(task.type as TaskType)] += 1;

      return {
        id: waypoint.id,
        seq: waypoint.sequence,
        addr: stopAddress(tasks),
        counts,
        total: tasks.length,
        status: stopStatus(tasks),
        eta: formatEta(waypoint.eta?.estimatedArrivalTime),
        contact: stopContact(tasks),
        window: stopWindow(tasks),
        note: tasks.some(taskHasVisibleNote),
        fragile: tasks.some((task) => task.fragile === true),
        heldCount: tasks.filter(isTaskOnCancellationHold).length,
        allHeld: tasks.length > 0 && tasks.every(isTaskOnCancellationHold),
        tasks,
      };
    });
}

/** Aggregate lane counts across all of a trip's stops. */
export function sumLaneCounts(stops: DerivedStop[]): LaneCounts {
  const total = emptyLaneCounts();
  for (const stop of stops) {
    total.pickup += stop.counts.pickup;
    total.delivery += stop.counts.delivery;
    total.dropoff += stop.counts.dropoff;
    total.return += stop.counts.return;
  }
  return total;
}
